/**
 * PurchaseSOSPage.tsx
 * Página de compra de créditos SOS
 * 
 * Fase 6 - Sistema de Pagamentos PIX
 * Data: 31/01/2026
 * 
 * Fluxo:
 * 1. Usuário seleciona quantidade (1-10 créditos)
 * 2. Seleciona método de pagamento (PIX, Cartão, Boleto)
 * 3. Preenche dados pessoais (se necessário)
 * 4. Confirma compra
 * 5. Exibe QR Code PIX ou processa cartão
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, CreditCard, ShoppingCart, Loader2, Check } from 'lucide-react';
import { FaPix } from 'react-icons/fa6';
import { PIXModal } from '../../components/payment/PIXModal';
import { supabase } from '../../lib/supabase';

const CREDIT_PRICE = 9.90; // R$ 9,90 por crédito

export default function PurchaseSOSPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'credit_card'>('pix');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal PIX
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState<{
    qr_code: string;
    qr_code_url: string;
    order_id: string;
  } | null>(null);

  // Dados do cliente (preenchidos automaticamente se já existir)
  const [customerData, setCustomerData] = useState({
    name: '',
    email: user?.email || '',
    document: '',
    phone: '',
    address: {
      line_1: '',
      zip_code: '',
      city: '',
      state: '',
      country: 'BR' as const
    }
  });

  const totalPrice = quantity * CREDIT_PRICE;

  const handlePurchase = async () => {
    if (!user) {
      setError('Você precisa estar logado para fazer uma compra');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Obter token de autenticação
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      // 2. Montar payload
      const payload: any = {
        quantity,
        payment_method: paymentMethod,
      };

      // 3. Se for primeira compra, incluir dados do cliente
      // (verificar se já existe customer_id no banco)
      const { data: subscription } = await supabase
        .from('user_subscriptions')
        .select('pagarme_customer_id')
        .eq('user_id', user.id)
        .single();

      if (!subscription?.pagarme_customer_id && paymentMethod === 'pix') {
        // Primeira compra - precisa dos dados
        if (!customerData.name || !customerData.document || !customerData.phone) {
          setError('Por favor, preencha todos os dados pessoais');
          setLoading(false);
          return;
        }
        payload.customer = customerData;
      }

      // 4. Chamar Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/purchase-sos-credits`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao processar compra');
      }

      const result = await response.json();

      // 5. Se PIX, exibir QR Code
      if (paymentMethod === 'pix') {
        setPixData({
          qr_code: result.pix_qr_code,
          qr_code_url: result.pix_qr_code_url,
          order_id: result.order_id,
        });
        setShowPixModal(true);
      }
      // 6. Se cartão, processar (implementar depois)
      else if (paymentMethod === 'credit_card') {
        // TODO: Implementar fluxo de cartão
        alert('Pagamento com cartão será implementado em breve');
      }

    } catch (err) {
      console.error('Erro ao processar compra:', err);
      setError(err instanceof Error ? err.message : 'Erro ao processar compra');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Voltar
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Título */}
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Comprar Consultas SOS</h1>
              <p className="text-sm text-gray-600">Adicione créditos à sua conta</p>
            </div>
          </div>

          {/* Erro */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}

          {/* Seleção de Quantidade */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Quantidade de Consultas
            </label>
            <div className="grid grid-cols-5 gap-3">
              {[1, 2, 3, 5, 10].map((qty) => (
                <button
                  key={qty}
                  onClick={() => setQuantity(qty)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    quantity === qty
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="text-2xl font-bold">{qty}</div>
                  <div className="text-xs mt-1">
                    R$ {(qty * CREDIT_PRICE).toFixed(2)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Seleção de Método de Pagamento */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Método de Pagamento
            </label>
            <div className="grid grid-cols-2 gap-4">
              {/* PIX */}
              <button
                onClick={() => setPaymentMethod('pix')}
                className={`p-6 rounded-xl border-2 transition-all ${
                  paymentMethod === 'pix'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <FaPix className={`w-12 h-12 mx-auto mb-2 ${
                  paymentMethod === 'pix' ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <div className={`font-semibold ${
                  paymentMethod === 'pix' ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  PIX
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Aprovação imediata
                </div>
              </button>

              {/* Cartão de Crédito */}
              <button
                onClick={() => setPaymentMethod('credit_card')}
                className={`p-6 rounded-xl border-2 transition-all ${
                  paymentMethod === 'credit_card'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <CreditCard className={`w-12 h-12 mx-auto mb-2 ${
                  paymentMethod === 'credit_card' ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <div className={`font-semibold ${
                  paymentMethod === 'credit_card' ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  Cartão
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Em breve
                </div>
              </button>
            </div>
          </div>

          {/* Dados Pessoais (apenas se PIX e primeira compra) */}
          {paymentMethod === 'pix' && (
            <div className="mb-8 space-y-4">
              <h3 className="font-semibold text-gray-900">Dados Pessoais</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    value={customerData.name}
                    onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="João Silva"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPF
                  </label>
                  <input
                    type="text"
                    value={customerData.document}
                    onChange={(e) => setCustomerData({ ...customerData, document: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="000.000.000-00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    value={customerData.phone}
                    onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CEP
                  </label>
                  <input
                    type="text"
                    value={customerData.address.zip_code}
                    onChange={(e) => setCustomerData({ 
                      ...customerData, 
                      address: { ...customerData.address, zip_code: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="00000-000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço
                </label>
                <input
                  type="text"
                  value={customerData.address.line_1}
                  onChange={(e) => setCustomerData({ 
                    ...customerData, 
                    address: { ...customerData.address, line_1: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Rua, número, complemento"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={customerData.address.city}
                    onChange={(e) => setCustomerData({ 
                      ...customerData, 
                      address: { ...customerData.address, city: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="São Paulo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <input
                    type="text"
                    value={customerData.address.state}
                    onChange={(e) => setCustomerData({ 
                      ...customerData, 
                      address: { ...customerData.address, state: e.target.value }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="SP"
                    maxLength={2}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Resumo e Botão */}
          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-6">
              <span className="text-lg text-gray-600">Total a pagar:</span>
              <span className="text-3xl font-bold text-blue-600">
                R$ {totalPrice.toFixed(2)}
              </span>
            </div>

            <button
              onClick={handlePurchase}
              disabled={loading || paymentMethod === 'credit_card'}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processando...
                </>
              ) : paymentMethod === 'credit_card' ? (
                'Em breve'
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Gerar PIX
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modal PIX */}
      {showPixModal && pixData && (
        <PIXModal
          qrCode={pixData.qr_code}
          qrCodeUrl={pixData.qr_code_url}
          amount={totalPrice}
          orderId={pixData.order_id}
          onClose={() => {
            setShowPixModal(false);
            navigate('/profile?tab=payment'); // Redirecionar após fechar
          }}
        />
      )}
    </div>
  );
}
